import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import PersonIcon from '@mui/icons-material/Person';
import {
  Box,
  Card,
  CardContent,
  Tab,
  Tabs,
  Typography,
  Stack,
  Alert,
  Skeleton,
  Avatar,
  Chip,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
} from '@mui/material';
import { memo, useState } from 'react';
import type { RSVP } from '@penticton-dev-events/shared';
import PendingRSVPsList from './PendingRSVPsList';

export type RSVPManagementPanelProps = {
  readonly pendingRSVPs: RSVP[];
  readonly approvedRSVPs: RSVP[];
  readonly rejectedRSVPs: RSVP[];
  readonly loading: boolean;
  readonly approvingIds: Set<string>;
  readonly rejectingIds: Set<string>;
  readonly batchApproving: boolean;
  readonly error: string | null;
  readonly onApprove: (userId: string) => Promise<void>;
  readonly onReject: (userId: string, reason?: string) => Promise<void>;
  readonly onBatchApprove: (userIds: string[]) => Promise<void>;
  readonly onClearError: () => void;
};

/**
 * Panel for managing RSVPs with tabs for pending, approved, and rejected
 */
const RSVPManagementPanel = memo<RSVPManagementPanelProps>(
  ({
    pendingRSVPs,
    approvedRSVPs,
    rejectedRSVPs,
    loading,
    approvingIds,
    rejectingIds,
    batchApproving,
    error,
    onApprove,
    onReject,
    onBatchApprove,
    onClearError,
  }) => {
    const [activeTab, setActiveTab] = useState<'pending' | 'approved' | 'rejected'>(
      'pending',
    );

    if (loading) {
      return (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              RSVP Management
            </Typography>
            <Stack spacing={2}>
              <Skeleton variant="rectangular" height={60} />
              <Skeleton variant="rectangular" height={60} />
              <Skeleton variant="rectangular" height={60} />
            </Stack>
          </CardContent>
        </Card>
      );
    }

    return (
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            RSVP Management
          </Typography>

          {/* Global Error */}
          {error && (
            <Alert severity="error" onClose={onClearError} sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {/* Tabs */}
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
            <Tabs
              value={activeTab}
              onChange={(_, value) => setActiveTab(value)}
              aria-label="RSVP management tabs"
            >
              <Tab
                icon={<HourglassEmptyIcon />}
                iconPosition="start"
                label={`Pending (${pendingRSVPs.length})`}
                value="pending"
              />
              <Tab
                icon={<CheckCircleIcon />}
                iconPosition="start"
                label={`Approved (${approvedRSVPs.length})`}
                value="approved"
              />
              <Tab
                icon={<CancelIcon />}
                iconPosition="start"
                label={`Rejected (${rejectedRSVPs.length})`}
                value="rejected"
              />
            </Tabs>
          </Box>

          {/* Tab Content */}
          <Box>
            {activeTab === 'pending' && (
              <PendingRSVPsList
                rsvps={pendingRSVPs}
                approvingIds={approvingIds}
                rejectingIds={rejectingIds}
                batchApproving={batchApproving}
                onApprove={onApprove}
                onReject={onReject}
                onBatchApprove={onBatchApprove}
              />
            )}

            {activeTab === 'approved' && (
              <>
                {approvedRSVPs.length === 0 ? (
                  <Alert severity="info">No approved RSVPs yet.</Alert>
                ) : (
                  <List>
                    {approvedRSVPs.map(rsvp => (
                      <ListItem key={rsvp.userId}>
                        <ListItemAvatar>
                          <Avatar src={rsvp.userPhotoURL} alt={rsvp.userDisplayName}>
                            {rsvp.userPhotoURL ? null : <PersonIcon />}
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={rsvp.userDisplayName}
                          secondary={
                            <>
                              {rsvp.userEmail && (
                                <Typography variant="caption" display="block">
                                  {rsvp.userEmail}
                                </Typography>
                              )}
                              <Typography variant="caption" color="text.secondary">
                                Approved:{' '}
                                {rsvp.approvedAt
                                  ? new Date(rsvp.approvedAt).toLocaleDateString()
                                  : 'N/A'}
                              </Typography>
                            </>
                          }
                        />
                        <Chip
                          icon={<CheckCircleIcon />}
                          label="Approved"
                          color="success"
                          size="small"
                        />
                      </ListItem>
                    ))}
                  </List>
                )}
              </>
            )}

            {activeTab === 'rejected' && (
              <>
                {rejectedRSVPs.length === 0 ? (
                  <Alert severity="info">No rejected RSVPs.</Alert>
                ) : (
                  <List>
                    {rejectedRSVPs.map(rsvp => (
                      <ListItem key={rsvp.userId} alignItems="flex-start">
                        <ListItemAvatar>
                          <Avatar src={rsvp.userPhotoURL} alt={rsvp.userDisplayName}>
                            {rsvp.userPhotoURL ? null : <PersonIcon />}
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={rsvp.userDisplayName}
                          secondary={
                            <>
                              {rsvp.userEmail && (
                                <Typography variant="caption" display="block">
                                  {rsvp.userEmail}
                                </Typography>
                              )}
                              <Typography variant="caption" color="text.secondary">
                                Rejected:{' '}
                                {rsvp.approvedAt
                                  ? new Date(rsvp.approvedAt).toLocaleDateString()
                                  : 'N/A'}
                              </Typography>
                              {rsvp.rejectionReason && (
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  display="block"
                                  sx={{ mt: 0.5 }}
                                >
                                  Reason: {rsvp.rejectionReason}
                                </Typography>
                              )}
                            </>
                          }
                        />
                        <Chip
                          icon={<CancelIcon />}
                          label="Rejected"
                          color="error"
                          size="small"
                        />
                      </ListItem>
                    ))}
                  </List>
                )}
              </>
            )}
          </Box>
        </CardContent>
      </Card>
    );
  },
);

RSVPManagementPanel.displayName = 'RSVPManagementPanel';

export default RSVPManagementPanel;
